import EntityOverview from "@/components/features/Entity/EntityOverview/EntityOverview";
import EntityPoc from "@/components/features/Entity/EntityPoc/EntityPoc";
import EntitySSO from "@/components/features/Entity/EntitySSO/EntitySSO";
import { Box, Stack } from "@mantine/core";
import { createFileRoute, useParams } from "@tanstack/react-router";
import styles from "./entityId.module.scss";
import { useEntityData } from "@/hooks/entities/useEntitiesQueries";
import Loader from "@/components/ui/Loader/Loader";
import { useMemo } from "react";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import EntityBranding from "@/components/features/Entity/EntityBranding/EntityBranding";
import EntityTemplates from "@/components/features/Entity/EntityTemplates/EntityTemplates";
import { EntityDocuments } from "@/components/features/Entity/EntityDocuments/EntityDocuments";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { authGuardWithPermissions } from "@/utils/authGuard";
import type { RouterContextType } from "@/api/permissions/permissions.api";

export const Route = createFileRoute("/_authenticated/entities/$entityId")({
  component: EntityInfo,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function EntityInfo() {
  const { entityId } = useParams({ from: "/_authenticated/entities/$entityId" });
  const { hasAccess } = usePermissions();
  const { edit: canEditEntity } = hasAccess({ module: MODULE_KEYS_MAP.entities });

  const { data: entityDetails, isLoading: entityDetailsLoading } = useEntityData(entityId);

  const entityName = entityDetails?.entityName;
  const hasValidName = entityName && entityName !== "N/A";

  const breadCrumbsItems = useMemo(() => {
    return [
      {
        title: "Entities",
        href: "/entities",
        icon: getRouteIcon(MODULE_KEYS_MAP.entities, 16),
      },
      {
        title: hasValidName ? entityName : "Loading...",
        href: `/entities/${entityId}`,
      },
    ];
  }, [hasValidName, entityName, entityId]);

  useBreadcrumb(breadCrumbsItems);

  if (entityDetailsLoading) {
    return <Loader />;
  }

  return (
    <Box className={styles.boxmain}>
      <Box className={styles.boxBody}>
        <Stack gap="var(--spacing-medium, 16px)">
          <EntityOverview entityDetails={entityDetails} />
          <EntityPoc entityDetails={entityDetails} />
          <EntitySSO entityDetails={entityDetails} />
          {entityDetails && <EntityBranding entityDetails={entityDetails} />}
          {entityDetails && (
            <EntityTemplates entityDetails={entityDetails} canEdit={canEditEntity} />
          )}
          <EntityDocuments entityId={entityId} canEdit={canEditEntity} />
        </Stack>
      </Box>
    </Box>
  );
}
